import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_HISTORY_DATA,
	URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
	URL_CMC_OPERATOR_AIRPORT_DATA,
	URL_CMC_OPERATOR_CONTACTS_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Faq } from "../sections/faq/Faq";
import { Pricing } from "../sections/pricing/Pricing";
import { ProductSlider } from "../sections/ProductSlider";
import { ProductTabs } from "../sections/ProductTabs";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { Trial } from "../sections/Trial";
import { Upgrades } from "../sections/Upgrades";
import { Hero } from "../sections/hero/Hero";
import { productSegmentPageProps } from "../../common/types/product";

export default function CommercialAircraftData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Access data on 82,000+ commercial aircraft worldwide, with detailed insights at the tail level."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Over 82,000 aircraft profiles",
							"Tail-level data, including specifications, seating, and utilization",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft Overview",
						description:
							"Gain a comprehensive, up-to-date aircraft data including:",
						list: [
							"IATA code",
							"Aircraft registration, C/N, MSN and variant info",
							"Status Info (who is operating the aircraft, is the aircraft active, aircraft age and MTOW)",
							"Aircraft insights (the exact model of aircraft, production line, and aircraft role)",
							"Aircraft hexcode",
							"Engine details (which engines are powering the aircraft, including engine type and subtype)",
							"ICAO engine emission databank id code",
							"Aircraft construction site",
							"Dates (when was the aircraft ordered and by whom, date of the first flight and delivery date)",
							"Seat info (number of seats, max. Abreast, pitch-in, in-seat video, in-seat power supply, if the aircraft has a usb port and which type; if the aircraft supports wireless charging, in-flight connectivity)",
							"Flight hours & cycles (total number of hours & cycles, avg. Stage length, avg. Annual hours & cycles, avg. Daily utilisation – including historical and forecasted data)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for aircraft worldwide using a range of filters, such as location, operator, aircraft type, engine type, seat configuration, utilisation, ownership, and more."
				slides={[
					{
						title: "Geographical",
						description: "Search for aircraft by location:",
						list: [
							"Continent",
							"Country",
							"Operator region",
							"Aircraft register",
							"Its current location",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-geography.jpg",
							title: "List of all scrapped/written off aircraft currently located at JFK International Airport",
						},
					},
					{
						title: "Operator",
						description: "Search for aircraft by:",
						list: [
							"Operator name",
							"Operator type",
							"Delivery customer",
							"Future operator",
							"ACMI customer",
							"Cargo customer",
							"Regional partnership",
							"IOSA certification",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-operator.jpg",
							title: "List of all IOSA-certified aircraft operated by scheduled carriers",
						},
					},
					{
						title: "Aircraft Type",
						description: "Search for aircraft by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft production line",
							"Aircraft family/type/model/variant",
							"IATA code",
							"Aircraft role",
							"Cargo loading configuration",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-aircraft-type.jpg",
							title: "List of all active Boeing 737 MAXs in Europe",
						},
					},
					{
						title: "Aircraft",
						description: "Search for aircraft by:",
						list: [
							"Aircraft registration",
							"Fleet number",
							"Aircraft line/serial number",
							"Aircraft block number",
							"Aircraft hexcode",
							"Aircraft status/sub-status",
							"Aircraft availability",
							"MTOW",
							"Construction site",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-aircraft-type.jpg",
							title: "Profile overview of the aircraft registration SX-NAC",
						},
					},
					{
						title: "Date",
						description:
							"Look into aircraft worldwide based on their:",
						list: [
							"Date built",
							"Delivery date",
							"Operational start",
							"Projected retirement",
							"Storage date",
							"Scrapped or written off date",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-date.jpg",
							title: "List of all Boeing aircraft delivered in January 2024",
						},
					},
					{
						title: "Orders",
						description: "Look into aircraft by:",
						list: [
							"Ordering entity",
							"Order date",
							"Cancellation date",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-orders.jpg",
							title: "List of all COMAC 919 ordered in 2024",
						},
					},
					{
						title: "Seating",
						description: "Search for aircraft by:",
						list: [
							"Cabin type",
							"Seat count",
							"Maximum abreast",
							"Pitch in",
							"Filter by features such as:",
							"In-seat video",
							"In-flight connectivity",
							"in-seat power supply",
							"Power source",
							"USB ports (including type)",
							"Wireless charging capability",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-seating.jpg",
							title: "List of all active Boeing aircraft with First and Business cabins and available wireless charging",
						},
					},
					{
						title: "Engines",
						description: "Search for aircraft by:",
						list: [
							"Engine manufacturer",
							"Engine family",
							"Enigne master series",
							"Engine type/subtype",
							"ICAO Engine Emission Databank ID",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-engines.jpg",
							title: "List of all active A220s in Europe equipped with Pratt & Whitney engines",
						},
					},
					{
						title: "Ownership",
						description: "Search for aircraft by:",
						list: [
							"Aircraft owner",
							"Aircraft manager",
							"Registered trust",
							"Ownership type",
							"Operating lease end dates",
							"Current market value",
							"Market lease rates",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-ownership.jpg",
							title: "Search for all B737 aircraft on operating leases with lease terms ending in 2028",
						},
					},
					{
						title: "Utilisation",
						description: "Search for aircraft based on:",
						list: [
							"Flight hours",
							"Flight cycles",
							"Average stage length",
							"Average daily utilisation",
							"Average annual hours",
							"Average annual cycles",
							"Forecasted hours ",
							"Forecasted cycles",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-utilisation.jpg",
							title: "The overview of all 737-800 projected to hit 18 000 Cycles in the next 5 years",
						},
					},
					{
						title: "Meta",
						description:
							"Search for all aircraft added to our database within a specific time period.",
						visual: {
							url: "/static/img/product/commercial-aircraft-data/aircraft-search-by-meta.jpg",
							title: "List of all aircraft added in the last 15 days",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 14,000 commercial operator and airport profiles, as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Operator Contacts Data",
						content:
							"Get a detailed contacts overview for almost any active as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_CONTACTS_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into aircraft ownership, management, valuation estimates, full lessor portfolios, and SPV-financed aircraft details.",
						url: URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft Historical Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_CMC_AIRCRAFT_HISTORY_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Global coverage: Access 82,000+ commercial aviation aircraft",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Commercial Aviation Aircraft Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Commercial Aviation Aircraft Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Aircraft Data can be purchased as a data
								feed for seamless integration into your own
								system. ch-aviation also provides full support
								for the integration process. To learn more about
								data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How often is the data updated?",
						content:
							"The Aircraft Data is being updated weekly, ensuring you always have the latest information.",
					},
				]}
			/>
		</>
	);
}
